import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import * as contrattiApi from "../../api/contratti";
import {
  AcquistoOLocazione,
  ProvenienzaApparato,
  RigaAccredito,
  RigaHardware,
  RigaManutenzione,
  SchedaEconomica,
  VoceListino,
} from "../../api/contratti";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { SedeAutocomplete } from "../../components/ui/SedeAutocomplete";

// ============================================================================
// LE RIGHE DI UNA SCHEDA ECONOMICA
//
// Fino al 27/08/2026 le righe si potevano scrivere solo da SQL: le tabelle
// c'erano, il PDF le stampava, e in mezzo non c'era niente. Questo è il pezzo
// che le fa compilare a chi il contratto lo prepara.
//
// ## Tre forme, non una con tutto opzionale
//
// Accredito descrive un servizio ricorrente su un punto vendita, Hardware un
// bene venduto, Manutenzione una voce scelta da un listino. Le colonne sono
// diverse e nessuna è un sottoinsieme dell'altra — è la stessa ragione per cui
// a database sono tre tabelle. Un form unico con metà campi nascosti a
// seconda del tipo direbbe che una riga di accredito può avere una marca.
//
// ## La manutenzione non si scrive, si SCEGLIE
//
// Le altre due schede hanno un form con dei campi. Quella di manutenzione no:
// si prende una voce dal listino dell'azienda, e quello che resta modificabile
// è il prezzo. Il testo (descrizione, copertura oraria, SLA) è una copia
// congelata di quella voce, e lasciarlo modificare produrrebbe una riga che
// non corrisponde più né al listino né a niente altro.
//
// ## Gli importi sono STRINGHE
//
// Prisma espone i `Decimal` come oggetti che in JSON diventano stringhe.
// Trattarli come numeri qui vorrebbe dire riconvertirli avanti e indietro e
// perdere gli zeri finali che il contratto scrive (`130,0000`).
// ============================================================================

/** `130.0000` → `130,00`. Vuoto resta vuoto: uno zero direbbe un'altra cosa. */
function importo(v: string | null | undefined, decimali = 2): string {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("it-IT", {
    minimumFractionDigits: decimali,
    maximumFractionDigits: decimali,
    useGrouping: true,
  });
}

/** Quello che si mette in un campo di testo partendo da un importo. */
function perModifica(v: string | null | undefined): string {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? String(n).replace(".", ",") : String(v);
}

function Cella({ children }: { children: React.ReactNode }) {
  return <td className="px-2 py-1.5 align-top text-xs text-ink/80">{children || <span className="text-ink/30">—</span>}</td>;
}

function Intestazione({ colonne }: { colonne: string[] }) {
  return (
    <thead className="bg-ink/[0.03]">
      <tr>
        {colonne.map((c) => (
          <th key={c} className="px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-ink/45">
            {c}
          </th>
        ))}
        <th className="w-px px-2" />
      </tr>
    </thead>
  );
}

function Azioni({
  modificabile,
  inCorso,
  onModifica,
  onElimina,
}: {
  modificabile: boolean;
  inCorso: boolean;
  onModifica?: () => void;
  onElimina: () => void;
}) {
  if (!modificabile) return <td />;
  return (
    <td className="whitespace-nowrap px-2 py-1.5 text-right">
      {onModifica && (
        <Button type="button" variant="ghost" size="sm" onClick={onModifica} aria-label="Modifica riga">
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      )}
      <Button type="button" variant="ghost" size="sm" loading={inCorso} onClick={onElimina} aria-label="Elimina riga">
        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
      </Button>
    </td>
  );
}

// ---------------------------------------------------------------- ACCREDITO

interface FormAccredito {
  sede_id: string;
  punto_vendita: string;
  indirizzo: string;
  tipo_servizio: string;
  durata_mesi: string;
  canone_mensile: string;
  percentuale_transato: string;
  accredito_su_hw_proprio: boolean;
  iban_versamento: string;
}

const ACCREDITO_VUOTA: FormAccredito = {
  sede_id: "",
  punto_vendita: "",
  indirizzo: "",
  tipo_servizio: "",
  durata_mesi: "",
  canone_mensile: "",
  percentuale_transato: "",
  accredito_su_hw_proprio: false,
  iban_versamento: "",
};

function ModaleAccredito({
  aperta,
  riga,
  onChiudi,
  onSalva,
}: {
  aperta: boolean;
  riga: RigaAccredito | null;
  onChiudi: () => void;
  onSalva: (payload: Record<string, unknown>) => Promise<void>;
}) {
  const [v, setV] = useState<FormAccredito>(ACCREDITO_VUOTA);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aperta) return;
    setV(
      riga
        ? {
            sede_id: riga.sede_id ?? "",
            punto_vendita: riga.punto_vendita,
            indirizzo: riga.indirizzo ?? "",
            tipo_servizio: riga.tipo_servizio ?? "",
            durata_mesi: riga.durata_mesi === null ? "" : String(riga.durata_mesi),
            canone_mensile: perModifica(riga.canone_mensile),
            percentuale_transato: perModifica(riga.percentuale_transato),
            accredito_su_hw_proprio: riga.accredito_su_hw_proprio,
            iban_versamento: riga.iban_versamento ?? "",
          }
        : ACCREDITO_VUOTA
    );
  }, [aperta, riga]);

  const invia = async () => {
    setSalvando(true);
    try {
      // Si manda TUTTO il form, anche i campi vuoti: qui il vuoto è una scelta
      // dell'operatore («questa riga non ha un transato»), e ometterlo in
      // modifica lascerebbe il valore di prima.
      await onSalva({
        sede_id: v.sede_id || null,
        punto_vendita: v.punto_vendita,
        indirizzo: v.indirizzo || null,
        tipo_servizio: v.tipo_servizio || null,
        durata_mesi: v.durata_mesi || null,
        canone_mensile: v.canone_mensile || null,
        percentuale_transato: v.percentuale_transato || null,
        accredito_su_hw_proprio: v.accredito_su_hw_proprio,
        iban_versamento: v.iban_versamento || null,
      });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      open={aperta}
      onClose={onChiudi}
      size="lg"
      title={riga ? "Modifica riga — Servizi Accredito" : "Nuova riga — Servizi Accredito"}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onChiudi}>
            Annulla
          </Button>
          <Button type="button" loading={salvando} onClick={invia}>
            Salva
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {/* Scegliendo una sede si riempiono da soli punto vendita, indirizzo e
            IBAN — quest'ultimo con la stessa regola della scheda Sede: quello
            del punto vendita se c'è, altrimenti quello dell'azienda. Restano
            tutti modificabili: sul contratto conta quello che c'è scritto. */}
        <SedeAutocomplete
          label="Punto vendita in anagrafica"
          value={v.punto_vendita}
          onChange={(t) => setV((s) => ({ ...s, punto_vendita: t }))}
          sedeId={v.sede_id}
          onSedeIdChange={(id) => setV((s) => ({ ...s, sede_id: id }))}
          placeholder="Cerca la sede, o scrivi il nome se non è censita"
        />
        <p className="-mt-1 text-xs text-ink/45">
          Lasciando i campi sotto in bianco, li compila il backend dalla sede scelta. Una sede di un altro cliente
          viene rifiutata.
        </p>
        <Input
          label="Indirizzo stampato"
          value={v.indirizzo}
          onChange={(e) => setV((s) => ({ ...s, indirizzo: e.target.value }))}
          placeholder="es. Piazza Castelnuovo, 26/c - 90141 Palermo (Pa)"
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Tipo servizio"
            value={v.tipo_servizio}
            onChange={(e) => setV((s) => ({ ...s, tipo_servizio: e.target.value }))}
            placeholder="es. K20"
          />
          <Input
            label="Durata (mesi)"
            inputMode="numeric"
            value={v.durata_mesi}
            onChange={(e) => setV((s) => ({ ...s, durata_mesi: e.target.value }))}
            placeholder="es. 60"
          />
          <Input
            label="Canone mensile"
            value={v.canone_mensile}
            onChange={(e) => setV((s) => ({ ...s, canone_mensile: e.target.value }))}
            placeholder="es. 130,00"
            hint="La virgola decimale va bene."
          />
          <Input
            label="% sul transato"
            value={v.percentuale_transato}
            onChange={(e) => setV((s) => ({ ...s, percentuale_transato: e.target.value }))}
            placeholder="es. 0,0027"
          />
        </div>
        <Input
          label="IBAN versamento"
          value={v.iban_versamento}
          onChange={(e) => setV((s) => ({ ...s, iban_versamento: e.target.value }))}
          placeholder="Lascia vuoto per prenderlo dalla sede o dal cliente"
        />
        <label className="flex items-center gap-2 text-sm text-ink/80">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-line"
            checked={v.accredito_su_hw_proprio}
            onChange={(e) => setV((s) => ({ ...s, accredito_su_hw_proprio: e.target.checked }))}
          />
          Accredito su hardware proprio del Cliente
        </label>
      </div>
    </Modal>
  );
}

// ----------------------------------------------------------------- HARDWARE

interface FormHardware {
  sede_id: string;
  punto_vendita: string;
  comune: string;
  provenienza: ProvenienzaApparato;
  marca: string;
  modello: string;
  attivazione_servizio: string;
  acquisto_o_locazione: AcquistoOLocazione;
  azienda_locatrice: string;
  garanzia_evo: boolean;
  garanzia_mesi: string;
  prezzo_unitario: string;
  quantita: string;
}

const HARDWARE_VUOTA: FormHardware = {
  sede_id: "",
  punto_vendita: "",
  comune: "",
  provenienza: "nuova_fornitura_evo",
  marca: "",
  modello: "",
  attivazione_servizio: "",
  acquisto_o_locazione: "acquisto",
  azienda_locatrice: "",
  garanzia_evo: false,
  garanzia_mesi: "",
  prezzo_unitario: "",
  quantita: "1",
};

function ModaleHardware({
  aperta,
  riga,
  onChiudi,
  onSalva,
}: {
  aperta: boolean;
  riga: RigaHardware | null;
  onChiudi: () => void;
  onSalva: (payload: Record<string, unknown>) => Promise<void>;
}) {
  const [v, setV] = useState<FormHardware>(HARDWARE_VUOTA);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aperta) return;
    setV(
      riga
        ? {
            sede_id: riga.sede_id ?? "",
            punto_vendita: riga.punto_vendita,
            comune: riga.comune ?? "",
            provenienza: riga.provenienza,
            marca: riga.marca ?? "",
            modello: riga.modello ?? "",
            attivazione_servizio: perModifica(riga.attivazione_servizio),
            acquisto_o_locazione: riga.acquisto_o_locazione,
            azienda_locatrice: riga.azienda_locatrice ?? "",
            garanzia_evo: riga.garanzia_evo,
            garanzia_mesi: riga.garanzia_mesi === null ? "" : String(riga.garanzia_mesi),
            prezzo_unitario: perModifica(riga.prezzo_unitario),
            quantita: String(riga.quantita),
          }
        : HARDWARE_VUOTA
    );
  }, [aperta, riga]);

  // Il form ANTICIPA le due regole del backend, non le riscrive: il controllo
  // vero resta nella rotta, perché lì passano anche le chiamate che dal form
  // non vengono. Qui servono a non far scrivere un modulo che verrà rifiutato.
  const inLocazione = v.acquisto_o_locazione === "locazione";
  const erroreLocatrice =
    !inLocazione && v.azienda_locatrice.trim() ? "Vale solo per una riga in locazione." : undefined;
  const erroreGaranzia =
    !v.garanzia_evo && v.garanzia_mesi.trim() ? "C'è una durata ma la garanzia Evo non è indicata." : undefined;

  const invia = async () => {
    setSalvando(true);
    try {
      await onSalva({
        sede_id: v.sede_id || null,
        punto_vendita: v.punto_vendita,
        comune: v.comune || null,
        provenienza: v.provenienza,
        marca: v.marca || null,
        modello: v.modello || null,
        attivazione_servizio: v.attivazione_servizio || null,
        acquisto_o_locazione: v.acquisto_o_locazione,
        azienda_locatrice: v.azienda_locatrice || null,
        garanzia_evo: v.garanzia_evo,
        garanzia_mesi: v.garanzia_mesi || null,
        prezzo_unitario: v.prezzo_unitario || null,
        quantita: v.quantita || "1",
      });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      open={aperta}
      onClose={onChiudi}
      size="lg"
      title={riga ? "Modifica riga — Hardware e Software" : "Nuova riga — Hardware e Software"}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onChiudi}>
            Annulla
          </Button>
          <Button
            type="button"
            loading={salvando}
            disabled={Boolean(erroreLocatrice || erroreGaranzia)}
            onClick={invia}
          >
            Salva
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <SedeAutocomplete
          label="Punto vendita in anagrafica"
          value={v.punto_vendita}
          onChange={(t) => setV((s) => ({ ...s, punto_vendita: t }))}
          sedeId={v.sede_id}
          onSedeIdChange={(id) => setV((s) => ({ ...s, sede_id: id }))}
          placeholder="Cerca la sede, o scrivi il nome se non è censita"
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Comune"
            value={v.comune}
            onChange={(e) => setV((s) => ({ ...s, comune: e.target.value }))}
            placeholder="Lascia vuoto per prenderlo dalla sede"
          />
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-ink/70">Apparato</span>
            <select
              className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm"
              value={v.provenienza}
              onChange={(e) => setV((s) => ({ ...s, provenienza: e.target.value as ProvenienzaApparato }))}
            >
              {(Object.keys(contrattiApi.PROVENIENZA_LABEL) as ProvenienzaApparato[]).map((p) => (
                <option key={p} value={p}>
                  {contrattiApi.PROVENIENZA_LABEL[p]}
                </option>
              ))}
            </select>
          </label>
          <Input label="Marca" value={v.marca} onChange={(e) => setV((s) => ({ ...s, marca: e.target.value }))} />
          <Input label="Modello" value={v.modello} onChange={(e) => setV((s) => ({ ...s, modello: e.target.value }))} />
        </div>

        <Input
          label="Attivazione servizio"
          value={v.attivazione_servizio}
          onChange={(e) => setV((s) => ({ ...s, attivazione_servizio: e.target.value }))}
          placeholder="es. 450,00"
          hint="Il costo una tantum previsto per le macchine non di nuova fornitura Evo."
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-ink/70">Acquisto o Locazione</span>
            <select
              className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm"
              value={v.acquisto_o_locazione}
              // Passando ad Acquisto si svuota anche la locatrice: senza,
              // il campo resta disabilitato CON dentro un valore, e «Salva»
              // resta bloccato finché non si torna su Locazione per
              // cancellarlo. Una via d'uscita c'era, ma passava da un giro
              // che nessuno indovina.
              onChange={(e) => {
                const scelta = e.target.value as AcquistoOLocazione;
                setV((s) => ({
                  ...s,
                  acquisto_o_locazione: scelta,
                  azienda_locatrice: scelta === "locazione" ? s.azienda_locatrice : "",
                }));
              }}
            >
              {(Object.keys(contrattiApi.ACQUISTO_LABEL) as AcquistoOLocazione[]).map((a) => (
                <option key={a} value={a}>
                  {contrattiApi.ACQUISTO_LABEL[a]}
                </option>
              ))}
            </select>
          </label>
          <Input
            label="Azienda locatrice"
            value={v.azienda_locatrice}
            onChange={(e) => setV((s) => ({ ...s, azienda_locatrice: e.target.value }))}
            disabled={!inLocazione}
            error={erroreLocatrice}
            placeholder={inLocazione ? "es. Alba Leasing S.p.A." : "Solo in locazione"}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <label className="flex items-center gap-2 pt-6 text-sm text-ink/80">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-line"
              checked={v.garanzia_evo}
              // Stessa ragione: togliendo la garanzia si svuota la durata.
              onChange={(e) => {
                const attiva = e.target.checked;
                setV((s) => ({ ...s, garanzia_evo: attiva, garanzia_mesi: attiva ? s.garanzia_mesi : "" }));
              }}
            />
            Garanzia Evo
          </label>
          <Input
            label="Durata garanzia (mesi)"
            inputMode="numeric"
            value={v.garanzia_mesi}
            onChange={(e) => setV((s) => ({ ...s, garanzia_mesi: e.target.value }))}
            disabled={!v.garanzia_evo}
            error={erroreGaranzia}
            placeholder={v.garanzia_evo ? "es. 60" : "Serve la garanzia Evo"}
          />
          <Input
            label="Q.tà"
            inputMode="numeric"
            value={v.quantita}
            onChange={(e) => setV((s) => ({ ...s, quantita: e.target.value }))}
          />
        </div>

        <Input
          label="Prezzo unitario (IVA esclusa)"
          value={v.prezzo_unitario}
          onChange={(e) => setV((s) => ({ ...s, prezzo_unitario: e.target.value }))}
          placeholder="es. 7.570,00"
          hint="Il totale di riga non si scrive: è prezzo × quantità, e una copia potrebbe discordare."
        />
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------- MANUTENZIONE

function ModaleManutenzione({
  aperta,
  riga,
  onChiudi,
  onSalva,
}: {
  aperta: boolean;
  riga: RigaManutenzione | null;
  onChiudi: () => void;
  onSalva: (payload: Record<string, unknown>) => Promise<void>;
}) {
  const [voci, setVoci] = useState<VoceListino[]>([]);
  const [voceId, setVoceId] = useState("");
  const [prezzo, setPrezzo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (!aperta) return;
    setVoceId(riga?.voce_listino_id ?? "");
    setPrezzo(perModifica(riga?.prezzo_applicato));
    if (riga) return; // in modifica il listino non serve: la voce non si cambia
    contrattiApi
      .listinoManutenzione()
      .then((d) => setVoci(d.voci))
      .catch((e) => showToast(getErrorMessage(e), "error"));
  }, [aperta, riga, showToast]);

  const voceScelta = voci.find((x) => x.id === voceId) ?? null;

  const invia = async () => {
    setSalvando(true);
    try {
      await onSalva(
        riga
          ? { prezzo_applicato: prezzo || null }
          : { voce_listino_id: voceId, ...(prezzo ? { prezzo_applicato: prezzo } : {}) }
      );
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      open={aperta}
      onClose={onChiudi}
      size="lg"
      title={riga ? `Prezzo — ${riga.nome}` : "Nuova voce — Manutenzione"}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onChiudi}>
            Annulla
          </Button>
          <Button type="button" loading={salvando} disabled={!riga && !voceId} onClick={invia}>
            Salva
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {riga ? (
          <p className="rounded-lg border border-line bg-ink/[0.02] px-3 py-2 text-xs text-ink/60">
            Di una voce già scelta si cambia solo il prezzo: descrizione, copertura e SLA sono la copia congelata del
            listino al momento in cui è stata messa sul contratto. Per cambiare voce, togli la riga e rimettila.
          </p>
        ) : (
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-ink/70">Voce di listino</span>
            <select
              className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm"
              value={voceId}
              onChange={(e) => {
                setVoceId(e.target.value);
                setPrezzo("");
              }}
            >
              <option value="">— scegli —</option>
              {(["a_chiamata", "canone_annuale"] as const).map((variante) => (
                <optgroup key={variante} label={contrattiApi.VARIANTE_LABEL[variante]}>
                  {voci
                    .filter((x) => x.variante === variante)
                    .map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.nome}
                        {x.prezzo ? ` — € ${importo(x.prezzo)}` : x.prezzo_nota ? ` — ${x.prezzo_nota}` : ""}
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
          </label>
        )}

        {voceScelta && (
          <dl className="rounded-lg border border-line px-3 py-2 text-xs text-ink/70">
            <div className="flex gap-2">
              <dt className="w-36 shrink-0 text-ink/45">Tipo contratto</dt>
              <dd>{voceScelta.tipo_contratto}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-36 shrink-0 text-ink/45">Descrizione</dt>
              <dd>{voceScelta.descrizione ?? "—"}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-36 shrink-0 text-ink/45">Copertura oraria</dt>
              <dd>{voceScelta.copertura_oraria ?? "—"}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-36 shrink-0 text-ink/45">SLA</dt>
              <dd>{voceScelta.sla ?? "—"}</dd>
            </div>
          </dl>
        )}

        <Input
          label={riga?.unita === "intervento" || voceScelta?.unita === "intervento" ? "Prezzo a intervento" : "Totale anno"}
          value={prezzo}
          onChange={(e) => setPrezzo(e.target.value)}
          placeholder={
            voceScelta?.prezzo
              ? `Lascia vuoto per il listino: ${importo(voceScelta.prezzo)}`
              : voceScelta?.prezzo_nota ?? "Lascia vuoto per il prezzo di listino"
          }
          hint="Eredita il listino se lo lasci in bianco, ed è ritoccabile per questo contratto."
        />
      </div>
    </Modal>
  );
}

// ============================================================================
// Il blocco che sta dentro la scheda
// ============================================================================

export function RigheScheda({
  scheda,
  modificabile,
  onCambiato,
}: {
  scheda: SchedaEconomica;
  /** Falso quando il contratto non è più una bozza: si legge e basta. */
  modificabile: boolean;
  onCambiato: () => void;
}) {
  const { showToast } = useToast();
  const [inCorso, setInCorso] = useState<string | null>(null);
  const [modaleAperta, setModaleAperta] = useState(false);
  const [inModifica, setInModifica] = useState<RigaAccredito | RigaHardware | RigaManutenzione | null>(null);

  const accredito = scheda.righe_accredito ?? [];
  const hardware = scheda.righe_hardware ?? [];
  const manutenzione = scheda.righe_manutenzione ?? [];
  const quante = accredito.length + hardware.length + manutenzione.length;

  const apri = (riga: RigaAccredito | RigaHardware | RigaManutenzione | null) => {
    setInModifica(riga);
    setModaleAperta(true);
  };

  const salva = useCallback(
    async (payload: Record<string, unknown>) => {
      try {
        if (inModifica) {
          await contrattiApi.aggiornaRiga(scheda.tipo, inModifica.id, payload);
          showToast("Riga aggiornata.", "success");
        } else {
          await contrattiApi.aggiungiRiga(scheda.id, payload);
          showToast("Riga aggiunta.", "success");
        }
        setModaleAperta(false);
        setInModifica(null);
        onCambiato();
      } catch (e) {
        // L'errore del backend si mostra così com'è: le sue frasi dicono
        // QUALE regola è stata infranta, e riscriverle qui vorrebbe dire
        // tenerne allineate due copie.
        showToast(getErrorMessage(e), "error");
      }
    },
    [inModifica, onCambiato, scheda.id, scheda.tipo, showToast]
  );

  const elimina = async (id: string) => {
    if (!window.confirm("Togliere questa riga dalla scheda?")) return;
    setInCorso(id);
    try {
      await contrattiApi.rimuoviRiga(scheda.tipo, id);
      showToast("Riga rimossa.", "success");
      onCambiato();
    } catch (e) {
      showToast(getErrorMessage(e), "error");
    } finally {
      setInCorso(null);
    }
  };

  const totaleHardware = hardware.reduce(
    (t, r) => t + Number(r.prezzo_unitario ?? 0) * r.quantita,
    0
  );

  return (
    <div className="mt-2 border-t border-line pt-2">
      {quante === 0 ? (
        <p className="px-1 py-2 text-xs text-ink/45">
          Nessuna riga: questa scheda è allegata ma non dice ancora niente. Sul PDF esce con l&apos;avviso che va
          compilata.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse">
            {scheda.tipo === "servizi_accredito" && (
              <>
                <Intestazione colonne={["Punto vendita", "Indirizzo", "Servizio", "Mesi", "Canone", "Transato", "HW proprio", "IBAN"]} />
                <tbody className="divide-y divide-line">
                  {accredito.map((r) => (
                    <tr key={r.id}>
                      <Cella>{r.punto_vendita}</Cella>
                      <Cella>{r.indirizzo}</Cella>
                      <Cella>{r.tipo_servizio}</Cella>
                      <Cella>{r.durata_mesi ?? ""}</Cella>
                      <Cella>{importo(r.canone_mensile)}</Cella>
                      <Cella>{importo(r.percentuale_transato, 4)}</Cella>
                      <Cella>{r.accredito_su_hw_proprio ? "Sì" : "No"}</Cella>
                      <Cella>
                        <span className="whitespace-nowrap font-mono text-[11px]">{r.iban_versamento}</span>
                      </Cella>
                      <Azioni
                        modificabile={modificabile}
                        inCorso={inCorso === r.id}
                        onModifica={() => apri(r)}
                        onElimina={() => elimina(r.id)}
                      />
                    </tr>
                  ))}
                </tbody>
              </>
            )}

            {scheda.tipo === "hardware_software" && (
              <>
                <Intestazione
                  colonne={["Punto vendita", "Comune", "Apparato", "Marca", "Modello", "Attivaz.", "Acq./Loc.", "Locatrice", "Gar.", "Mesi", "Prezzo", "Q.tà", "Totale"]}
                />
                <tbody className="divide-y divide-line">
                  {hardware.map((r) => (
                    <tr key={r.id}>
                      <Cella>{r.punto_vendita}</Cella>
                      <Cella>{r.comune}</Cella>
                      <Cella>{contrattiApi.PROVENIENZA_LABEL[r.provenienza]}</Cella>
                      <Cella>{r.marca}</Cella>
                      <Cella>{r.modello}</Cella>
                      <Cella>{importo(r.attivazione_servizio)}</Cella>
                      <Cella>{contrattiApi.ACQUISTO_LABEL[r.acquisto_o_locazione]}</Cella>
                      <Cella>{r.azienda_locatrice}</Cella>
                      <Cella>{r.garanzia_evo ? "Sì" : "No"}</Cella>
                      <Cella>{r.garanzia_mesi ?? ""}</Cella>
                      <Cella>{importo(r.prezzo_unitario)}</Cella>
                      <Cella>{r.quantita}</Cella>
                      <Cella>{importo(String(Number(r.prezzo_unitario ?? 0) * r.quantita))}</Cella>
                      <Azioni
                        modificabile={modificabile}
                        inCorso={inCorso === r.id}
                        onModifica={() => apri(r)}
                        onElimina={() => elimina(r.id)}
                      />
                    </tr>
                  ))}
                  {hardware.length > 0 && (
                    <tr>
                      <td colSpan={12} className="px-2 py-1.5 text-right text-xs font-semibold text-ink/60">
                        Totale (IVA esclusa)
                      </td>
                      <td className="px-2 py-1.5 text-xs font-bold text-ink">{importo(String(totaleHardware))}</td>
                      <td />
                    </tr>
                  )}
                </tbody>
              </>
            )}

            {scheda.tipo === "manutenzione_hardware_software" && (
              <>
                <Intestazione colonne={["Variante", "Nome", "Descrizione", "Copertura", "SLA", "Ricambi / Obbl.", "Prezzo"]} />
                <tbody className="divide-y divide-line">
                  {manutenzione.map((r) => (
                    <tr key={r.id}>
                      <Cella>{contrattiApi.VARIANTE_LABEL[r.variante]}</Cella>
                      <Cella>{r.nome}</Cella>
                      <Cella>{r.descrizione}</Cella>
                      <Cella>{r.copertura_oraria}</Cella>
                      <Cella>{r.sla}</Cella>
                      <Cella>{r.ricambi ?? (r.obbligatoria === null ? "" : r.obbligatoria ? "si" : "no")}</Cella>
                      <Cella>
                        {/* La cifra quando c'è, il testo quando non c'è: a
                            listino una voce su sei dice «A consuntivo», e uno
                            0,00 al suo posto direbbe gratis. */}
                        {r.prezzo_applicato ? `€ ${importo(r.prezzo_applicato)}` : r.prezzo_nota}
                      </Cella>
                      <Azioni
                        modificabile={modificabile}
                        inCorso={inCorso === r.id}
                        onModifica={() => apri(r)}
                        onElimina={() => elimina(r.id)}
                      />
                    </tr>
                  ))}
                </tbody>
              </>
            )}
          </table>
        </div>
      )}

      {modificabile && (
        <Button type="button" variant="ghost" size="sm" className="mt-2" onClick={() => apri(null)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {scheda.tipo === "manutenzione_hardware_software" ? "Aggiungi voce di listino" : "Aggiungi riga"}
        </Button>
      )}

      {scheda.tipo === "servizi_accredito" && (
        <ModaleAccredito
          aperta={modaleAperta}
          riga={inModifica as RigaAccredito | null}
          onChiudi={() => setModaleAperta(false)}
          onSalva={salva}
        />
      )}
      {scheda.tipo === "hardware_software" && (
        <ModaleHardware
          aperta={modaleAperta}
          riga={inModifica as RigaHardware | null}
          onChiudi={() => setModaleAperta(false)}
          onSalva={salva}
        />
      )}
      {scheda.tipo === "manutenzione_hardware_software" && (
        <ModaleManutenzione
          aperta={modaleAperta}
          riga={inModifica as RigaManutenzione | null}
          onChiudi={() => setModaleAperta(false)}
          onSalva={salva}
        />
      )}
    </div>
  );
}
